/**
 * F2 Phase 2.c - Admin referrals CMS + analytics.
 *
 * Widgets:
 *   AdminReferralAnalyticsCard   - funnel + top referrers + fraud flags
 *   AdminReferralsPanel          - paginated review queue + drawer
 *   AdminReferralCodeManager     - disable/enable a referral code
 *
 * Middle-dot separators. Zero em/en-dashes. Ember/navy palette via existing
 * shadcn primitives.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/utils/api';

const STATUSES = [
  { value: 'any', label: 'Any status' },
  { value: 'attributed', label: 'Attributed' },
  { value: 'converted', label: 'Converted' },
  { value: 'rate_limit_capped', label: 'Rate-limit capped' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_COLOR = {
  attributed: 'bg-amber-50 text-amber-800 border-amber-200',
  converted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  rate_limit_capped: 'bg-orange-50 text-orange-800 border-orange-200',
  rejected: 'bg-red-50 text-red-800 border-red-200',
};

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
const AdminReferralAnalyticsCard = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/referrals/analytics')
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const funnel = data?.funnel || {};
  const flags = data?.flags || {};
  const top = data?.top_referrers || [];
  const maxFunnel = Math.max(1,
    funnel.landed || 0, funnel.attributed || 0, funnel.converted || 0);

  const bar = (n, cls) => (
    <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
      <div
        className={`h-full ${cls}`}
        style={{ width: `${Math.min(100, ((n || 0) / maxFunnel) * 100)}%` }}
      />
    </div>
  );

  return (
    <Card data-testid="admin-referral-analytics-card">
      <CardHeader>
        <CardTitle className="text-base">Referral funnel</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {[
          ['Landed', funnel.landed, 'bg-navy-700', 'landed'],
          ['Attributed', funnel.attributed, 'bg-navy-500', 'attributed'],
          ['Converted', funnel.converted, 'bg-amber-500', 'converted'],
          ['Points awarded', funnel.points_awarded, 'bg-emerald-500', 'points_awarded'],
          ['Rate-limit capped', funnel.rate_limit_capped, 'bg-orange-500', 'capped'],
        ].map(([label, n, cls, key]) => (
          <div key={key} data-testid={`analytics-funnel-${key}`}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-neutral-600">{label}</span>
              <span className="font-mono text-neutral-900">{n ?? 0}</span>
            </div>
            {bar(n, cls)}
          </div>
        ))}

        <div className="pt-3 border-t border-neutral-200">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
            Fraud flag counts
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(flags).map(([k, v]) => (
              <Badge
                key={k}
                variant="outline"
                data-testid={`analytics-flag-${k}`}
                className="font-mono"
              >
                {k} : {v}
              </Badge>
            ))}
          </div>
        </div>

        <div className="pt-3 border-t border-neutral-200">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
            Top referrers
          </div>
          {top.length === 0 ? (
            <div className="text-sm text-neutral-500">No conversions yet.</div>
          ) : (
            <ul className="space-y-1" data-testid="analytics-top-referrers">
              {top.slice(0, 5).map((r, i) => (
                <li
                  key={r.user_id}
                  className="flex justify-between text-sm"
                  data-testid={`analytics-top-referrer-${i}`}
                >
                  <span>
                    {i + 1}. {r.display_name}
                    <span className="text-neutral-400 text-xs ml-2 font-mono">
                      {r.email_masked}
                    </span>
                  </span>
                  <span className="font-mono text-neutral-700">
                    {r.conversions} · {r.points} pts
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Code manager
// ---------------------------------------------------------------------------
const AdminReferralCodeManager = () => {
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    if (!code.trim() || reason.trim().length < 5) {
      toast.error('Enter a code and a reason (5+ characters).');
      return;
    }
    setBusy(true);
    try {
      const r = await api.patch(
        `/admin/referral-codes/${encodeURIComponent(code.trim())}/${action}`,
        { reason: reason.trim() },
      );
      toast.success(`Code ${r.data.code} is now ${r.data.active ? 'active' : 'disabled'}.`);
      setCode(''); setReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action} code.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="admin-referral-code-manager">
      <CardHeader>
        <CardTitle className="text-base">Referral code lookup</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="Code (e.g., K7F9M2P)"
          data-testid="admin-referral-code-input"
          className="font-mono"
        />
        <Textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason (required, 5+ characters)"
          rows={2}
          data-testid="admin-referral-code-reason"
        />
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => act('disable')}
            disabled={busy}
            data-testid="admin-referral-code-disable"
          >
            Disable
          </Button>
          <Button
            variant="outline"
            onClick={() => act('enable')}
            disabled={busy}
            data-testid="admin-referral-code-enable"
          >
            Enable
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Review panel
// ---------------------------------------------------------------------------
const AdminReferralsPanel = () => {
  const [filters, setFilters] = useState({
    status: 'any', has_fraud_flag: false, referrer_user_id: '',
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(null); // {row, detail, reason, submitting}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filters.status && filters.status !== 'any') params.status = filters.status;
      if (filters.has_fraud_flag) params.has_fraud_flag = true;
      if (filters.referrer_user_id.trim()) {
        params.referrer_user_id = filters.referrer_user_id.trim();
      }
      const r = await api.get('/admin/referrals', { params });
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load referrals.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (row) => {
    setDrawer({ row, detail: null, reason: '', submitting: false });
    try {
      const r = await api.get(`/admin/referrals/${row.id}`);
      setDrawer(d => d ? { ...d, detail: r.data } : d);
    } catch {
      // ignore
    }
  };

  const submitReview = async (decision) => {
    if (!drawer) return;
    if (drawer.reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters.');
      return;
    }
    setDrawer(d => ({ ...d, submitting: true }));
    try {
      await api.patch(
        `/admin/referrals/${drawer.row.id}/review`,
        { decision, reason: drawer.reason.trim() },
      );
      toast.success(`Marked ${decision}.`);
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed.');
      setDrawer(d => ({ ...d, submitting: false }));
    }
  };

  return (
    <Card data-testid="admin-referrals-panel">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <CardTitle className="text-base">Referral attributions</CardTitle>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Select
              value={filters.status}
              onValueChange={v => setFilters(f => ({ ...f, status: v }))}
            >
              <SelectTrigger
                className="w-40"
                data-testid="admin-referrals-filter-status"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={filters.has_fraud_flag}
                onChange={e => setFilters(f => ({ ...f, has_fraud_flag: e.target.checked }))}
                data-testid="admin-referrals-filter-has-fraud-flag"
              />
              With fraud flag
            </label>
            <Input
              value={filters.referrer_user_id}
              onChange={e => setFilters(f => ({ ...f, referrer_user_id: e.target.value }))}
              placeholder="Referrer id"
              className="w-48 font-mono text-xs"
              data-testid="admin-referrals-filter-referrer"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-neutral-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-500" data-testid="admin-referrals-empty">
            No attributions match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Referrer</th>
                  <th className="py-2 pr-4">Referred</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Flags</th>
                  <th className="py-2 pr-4 text-right">Points</th>
                  <th className="py-2 pr-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr
                    key={r.id}
                    className="border-t border-neutral-100"
                    data-testid={`admin-referrals-row-${r.id}`}
                  >
                    <td className="py-2 pr-4 font-mono text-xs text-neutral-500">
                      {(r.attributed_at || '').slice(0, 16).replace('T', ' ')}
                    </td>
                    <td className="py-2 pr-4">
                      <div>{r.referrer?.display_name}</div>
                      <div className="text-xs font-mono text-neutral-400">
                        {r.referral_code}
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      <div>{r.referred?.display_name}</div>
                      <div className="text-xs font-mono text-neutral-400">
                        {r.referred?.email_masked}
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      <Badge
                        variant="outline"
                        className={STATUS_COLOR[r.status] || ''}
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {(r.flags || []).map(f => (
                          <Badge key={f} variant="outline" className="text-[10px]">
                            {f}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-right font-mono">
                      {(r.points_awarded?.signup || 0) + (r.points_awarded?.conversion || 0)}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDrawer(r)}
                        data-testid={`admin-referrals-review-${r.id}`}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent
          className="w-[520px] sm:max-w-[520px] overflow-y-auto"
          data-testid="admin-referrals-review-drawer"
        >
          <SheetHeader>
            <SheetTitle>Attribution review</SheetTitle>
          </SheetHeader>
          {drawer ? (
            <div className="mt-4 space-y-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                  Referrer
                </div>
                <div>{drawer.row.referrer?.display_name}</div>
                <div className="text-xs font-mono text-neutral-400">
                  {drawer.row.referrer?.email_masked}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                  Referred
                </div>
                <div>{drawer.row.referred?.display_name}</div>
                <div className="text-xs font-mono text-neutral-400">
                  {drawer.row.referred?.email_masked}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                  Code · Via
                </div>
                <div className="font-mono">
                  {drawer.row.referral_code} · {drawer.row.attributed_via}
                </div>
              </div>
              {(drawer.row.flags || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                    Flags
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {drawer.row.flags.map(f => (
                      <Badge key={f} variant="outline">{f}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                  Points on ledger
                </div>
                <div className="font-mono text-xs">
                  signup: {drawer.row.points_awarded?.signup || 0}
                  {' · '}conversion: {drawer.row.points_awarded?.conversion || 0}
                </div>
              </div>
              {drawer.detail?.landings?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                    Landings ({drawer.detail.landings.length})
                  </div>
                  <ul className="text-xs font-mono text-neutral-500 space-y-1">
                    {drawer.detail.landings.slice(0, 3).map(l => (
                      <li key={l.id}>
                        {(l.landed_at || '').slice(0, 16).replace('T', ' ')} · ip {l.ip_hash?.slice(0, 8)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                  Reason (required)
                </div>
                <Textarea
                  value={drawer.reason}
                  onChange={e => setDrawer(d => ({ ...d, reason: e.target.value }))}
                  rows={3}
                  placeholder="Why are you approving or rejecting this attribution?"
                  data-testid="admin-referrals-review-reason"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => submitReview('approved')}
                  disabled={drawer.submitting}
                  data-testid="admin-referrals-approve"
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => submitReview('rejected')}
                  disabled={drawer.submitting}
                  data-testid="admin-referrals-reject"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  Reject and revoke
                </Button>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
const AdminReferralsPage = () => {
  return (
    <div className="p-6 space-y-6" data-testid="admin-referrals-page">
      <h1 className="font-display text-2xl font-bold text-primary-900">
        Referrals
      </h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <AdminReferralsPanel />
        </div>
        <div className="space-y-4">
          <AdminReferralAnalyticsCard />
          <AdminReferralCodeManager />
        </div>
      </div>
    </div>
  );
};

export default AdminReferralsPage;
